import Take3D from "../models/Take3D";
import Interaction, { InteractionType } from "../models/interactions/Interaction";
import { ISequence } from "../models/interactions/SequenceModel";
import { ISendActorToPosition } from "../models/interactions/SendActorToPosition";
import { ISetCamera } from "../models/interactions/SetCamera";
import { IAttach } from "../models/interactions/Attach";
import { IDetach } from "../models/interactions/Detach";
import { IGrab } from "../models/interactions/Grab";
// Type-only: loading the Trigger model pulls in its node preview components.
import type { ITrigger, TriggerType } from "../models/interactions/Trigger";

const ON_TAKE_START = "onStart" as TriggerType;
const ON_TAKE_END = "onEnd" as TriggerType;

/**
 * Where something is once a node has run. More than one value when it can't be
 * told apart: parallel branches that both change it in no fixed order.
 */
export interface StateValue {
  values: string[];
  /** Changed by a node or interaction, rather than the take's own state. */
  changed: boolean;
}

export interface SequenceStateAtNode {
  /** actorId → where the actor is: see parseActorPlace. */
  actors: Record<string, StateValue>;
  /** propId → who holds the prop (Prop to Player): see parseHeldProp. Only props a hand is involved with. */
  heldProps: Record<string, StateValue>;
  camera?: StateValue;
}

const RIDING = "riding:";
const BESIDE = "beside:";
const HELD_BY = "heldBy:";
const GONE = "gone";

export type ActorPlace =
  | { kind: "position"; positionId: string }
  /** On a prop (Player to Prop): drawn with the prop, nowhere else. */
  | { kind: "riding"; propId: string }
  /** Got off a prop (Detach): stands where the prop is. */
  | { kind: "beside"; propId: string };

export const parseActorPlace = (value: string): ActorPlace =>
  value.startsWith(RIDING)
    ? { kind: "riding", propId: value.slice(RIDING.length) }
    : value.startsWith(BESIDE)
      ? { kind: "beside", propId: value.slice(BESIDE.length) }
      : { kind: "position", positionId: value };

/** Held by an actor (Prop to Player), or gone: a detached hand prop is destroyed. */
export type HeldProp = { kind: "heldBy"; actorId: string } | { kind: "gone" };

export const parseHeldProp = (value: string): HeldProp =>
  value === GONE ? { kind: "gone" } : { kind: "heldBy", actorId: value.slice(HELD_BY.length) };

type Edge = NonNullable<ISequence["edges"]>[number];
type Move = { key: string; value: string };

interface MoveContext {
  /** Props actors get onto (Player to Prop) rather than take in a hand. */
  ridePropIds: Set<string>;
}

const actorKey = (actorId: string) => `actor:${actorId}`;
const heldKey = (propId: string) => `held:${propId}`;

/** What a node or interaction changes, in the order the compiled game sees it. */
const movesOf = (interaction: Interaction, context: MoveContext): Move[] => {
  if (interaction.disabled) return [];
  switch (interaction.type) {
    case InteractionType.SendActorToPosition: {
      const send = interaction as ISendActorToPosition;
      // Sending an actor off a prop gets it off first (the compiler detaches).
      return send.npcId && send.positionId
        ? [{ key: actorKey(send.npcId), value: send.positionId }]
        : [];
    }
    case InteractionType.SetCamera: {
      const camera = interaction as ISetCamera;
      return camera.cameraId ? [{ key: "camera", value: camera.cameraId }] : [];
    }
    case InteractionType.Attach: {
      const attach = interaction as IAttach;
      if (!attach.propGuid || !attach.playerGuid) return [];
      return attach.playerToProp
        ? [{ key: actorKey(attach.playerGuid), value: RIDING + attach.propGuid }]
        : [{ key: heldKey(attach.propGuid), value: HELD_BY + attach.playerGuid }];
    }
    case InteractionType.Grab: {
      const grab = interaction as IGrab;
      return grab.propGuid && grab.playerGuid
        ? [{ key: heldKey(grab.propGuid), value: HELD_BY + grab.playerGuid }]
        : [];
    }
    case InteractionType.Detach: {
      const detach = interaction as IDetach;
      if (!detach.propGuid) return [];
      if (context.ridePropIds.has(detach.propGuid))
        return detach.playerGuid
          ? [{ key: actorKey(detach.playerGuid), value: BESIDE + detach.propGuid }]
          : [];
      return [{ key: heldKey(detach.propGuid), value: GONE }];
    }
    default:
      return [];
  }
};

/** A path out of an If or Switch through one of its branches, not the node's own output. */
const isBranchEdge = (edge: Edge, typeOf: (nodeId: string) => string | undefined) => {
  const type = typeOf(edge.source);
  return (
    (type === InteractionType.If || type === InteractionType.Switch) &&
    (edge.sourceHandle ?? "source") !== "source"
  );
};

class Graph {
  private readonly outgoing = new Map<string, Edge[]>();
  private readonly incoming = new Map<string, Edge[]>();
  private readonly types = new Map<string, string>();
  readonly interactions = new Map<string, Interaction>();

  constructor(sequence: ISequence) {
    (sequence.nodes ?? []).forEach((node) => {
      this.types.set(node.id, node.data.interaction.type);
      this.interactions.set(node.id, node.data.interaction);
    });
    (sequence.edges ?? []).forEach((edge) => {
      this.outgoing.set(edge.source, [...(this.outgoing.get(edge.source) ?? []), edge]);
      this.incoming.set(edge.target, [...(this.incoming.get(edge.target) ?? []), edge]);
    });
  }

  private walk(from: string[], next: (nodeId: string) => string[]) {
    const seen = new Set<string>();
    const queue = [...from];
    while (queue.length > 0) {
      const nodeId = queue.shift()!;
      if (seen.has(nodeId)) continue;
      seen.add(nodeId);
      queue.push(...next(nodeId));
    }
    return seen;
  }

  reachableFrom(nodeIds: string[]) {
    return this.walk(nodeIds, (id) => (this.outgoing.get(id) ?? []).map((edge) => edge.target));
  }

  /**
   * Nodes that run beside `nodeId`: from the same start, but neither before nor
   * after it. A node after the branches join comes after it, so it isn't one.
   */
  parallelTo(nodeId: string, ancestors: Set<string>) {
    const roots = [...ancestors, nodeId].filter((id) => !this.incoming.has(id));
    const after = this.reachableFrom([nodeId]);
    const parallel = this.reachableFrom(roots);
    parallel.forEach((id) => {
      if (ancestors.has(id) || after.has(id)) parallel.delete(id);
    });
    return parallel;
  }

  ancestorsOf(nodeId: string) {
    const ancestors = this.walk([nodeId], (id) =>
      (this.incoming.get(id) ?? []).map((edge) => edge.source),
    );
    ancestors.delete(nodeId);
    return ancestors;
  }

  /**
   * Nodes that only run on some branches of an If or Switch. They're left out
   * of the state, except those in the branch `nodeId` itself is in.
   */
  branchOnlyNodes(nodeId?: string) {
    const typeOf = (id: string) => this.types.get(id);
    const skipped = new Set<string>();
    this.types.forEach((type, sourceId) => {
      if (type !== InteractionType.If && type !== InteractionType.Switch) return;
      const outgoing = this.outgoing.get(sourceId) ?? [];
      const branches = outgoing
        .filter((edge) => isBranchEdge(edge, typeOf))
        .map((edge) => this.reachableFrom([edge.target]));
      if (branches.length === 0) return;
      // Reached from the node's own output too: runs whichever branch is taken.
      const always = this.reachableFrom(
        outgoing.filter((edge) => !isBranchEdge(edge, typeOf)).map((edge) => edge.target),
      );
      // A node the If's own output also reaches runs either way: it isn't in a branch.
      const own = nodeId && !always.has(nodeId) ? branches.filter((branch) => branch.has(nodeId)) : [];
      branches.forEach((branch) =>
        branch.forEach((id) => {
          // With one branch wired, the other path does nothing: its nodes are conditional too.
          const inEveryBranch = branches.length > 1 && branches.every((other) => other.has(id));
          const inOwnBranch = own.length === 1 && own[0].has(id);
          if (!inEveryBranch && !inOwnBranch && !always.has(id)) skipped.add(id);
        }),
      );
    });
    return skipped;
  }

  triggerOf(nodeId: string) {
    const roots = [nodeId, ...this.ancestorsOf(nodeId)];
    const trigger = roots
      .map((id) => this.interactions.get(id))
      .find((interaction) => interaction?.type === InteractionType.Trigger) as
      | ITrigger
      | undefined;
    return trigger?.triggerType;
  }

  triggerNodeId(triggerType: TriggerType) {
    return [...this.interactions.entries()].find(
      ([, interaction]) =>
        interaction.type === InteractionType.Trigger &&
        (interaction as ITrigger).triggerType === triggerType,
    )?.[0];
  }
}

/**
 * Applies the moves of `nodeIds` (a part of `graph`): per actor or camera, the
 * last move is the one every other move leads to. Moves with no such order
 * (parallel branches) leave every candidate.
 */
function applyGraphMoves(
  state: Map<string, StateValue>,
  graph: Graph,
  nodeIds: Iterable<string>,
  context: MoveContext,
) {
  const movesByKey = new Map<string, { nodeId: string; move: Move }[]>();
  [...nodeIds].forEach((nodeId) => {
    const interaction = graph.interactions.get(nodeId);
    if (!interaction) return;
    movesOf(interaction, context).forEach((move) =>
      movesByKey.set(move.key, [...(movesByKey.get(move.key) ?? []), { nodeId, move }]),
    );
  });

  movesByKey.forEach((moves, key) => {
    const last = moves.filter(
      ({ nodeId }) =>
        !moves.some(
          (other) => other.nodeId !== nodeId && graph.reachableFrom([nodeId]).has(other.nodeId),
        ),
    );
    state.set(key, {
      values: [...new Set((last.length > 0 ? last : moves).map(({ move }) => move.value))],
      changed: true,
    });
  });
  return new Set(movesByKey.keys());
}

/**
 * Applies the node's own path and the branches beside it, those branches counted
 * as done. Where a branch and the node's path change the same thing in no fixed
 * order, the path wins: the other branches finished first. Two other branches
 * that race each other stay uncertain.
 */
function applyPathAndParallelBranches(
  state: Map<string, StateValue>,
  graph: Graph,
  path: string[],
  parallel: string[],
  context: MoveContext,
) {
  const pathState = new Map(state);
  const pathKeys = applyGraphMoves(pathState, graph, path, context);
  applyGraphMoves(state, graph, [...path, ...parallel], context);
  pathKeys.forEach((key) => {
    const merged = state.get(key);
    const own = pathState.get(key);
    if (merged && own && merged.values.length > 1 && own.values.some((value) => merged.values.includes(value)))
      state.set(key, own);
  });
}

/** Everything a whole sequence (or one trigger's graph) leaves, skipping branch-only nodes. */
function applyWholeSequence(
  state: Map<string, StateValue>,
  sequence: ISequence,
  context: MoveContext,
  rootId?: string,
) {
  const graph = new Graph(sequence);
  const skipped = graph.branchOnlyNodes();
  const nodeIds = rootId
    ? graph.reachableFrom([rootId])
    : new Set(graph.interactions.keys());
  applyGraphMoves(
    state,
    graph,
    [...nodeIds].filter((id) => !skipped.has(id)),
    context,
  );
}

function applyInteraction(
  state: Map<string, StateValue>,
  interaction: Interaction,
  context: MoveContext,
) {
  if (interaction.disabled) return;
  if (interaction.type === InteractionType.Sequence) {
    applyWholeSequence(state, interaction as ISequence, context);
    return;
  }
  movesOf(interaction, context).forEach((move) =>
    state.set(move.key, { values: [move.value], changed: true }),
  );
}

/**
 * Props an actor gets onto (Player to Prop): a Detach on one gets the actor
 * off, where on any other prop it takes it out of the hand.
 */
function getRidePropIds(take: Take3D, sequence?: ISequence): Set<string> {
  const ids = new Set<string>();
  Object.entries(take.attachments ?? {}).forEach(([propId, attachment]) => {
    if (attachment.playerToProp) ids.add(propId);
  });
  const visit = (interaction: Interaction) => {
    if (interaction.type === InteractionType.Attach) {
      const attach = interaction as IAttach;
      if (attach.playerToProp && attach.propGuid) ids.add(attach.propGuid);
    }
    if (interaction.type === InteractionType.Sequence)
      ((interaction as ISequence).nodes ?? []).forEach((node) => visit(node.data.interaction));
  };
  (take.interactions ?? []).forEach(visit);
  [take.sequence, sequence].forEach((graph) =>
    (graph?.nodes ?? []).forEach((node) => visit(node.data.interaction)),
  );
  return ids;
}

/**
 * Where the actors and the camera are once `nodeId` has run, following
 * everything the take does before it: the take's own state, then (for a node
 * after the take start) On Take Start and the interactions before it, then the
 * nodes on the way to it and the node itself. Parallel branches beside the node
 * count as done; where one and the node's path change the same thing, the path
 * wins. If and Switch branches are skipped unless the node is in one.
 *
 * `sequence` is the take's sequence or a Sequence interaction in the take.
 */
/** The take's own state, before anything in it runs. */
function getTakeSetupState(take: Take3D) {
  const state = new Map<string, StateValue>();
  (take.actors ?? []).forEach((actor) =>
    state.set(actorKey(actor.actorId), { values: [actor.positionId], changed: false }),
  );
  // The take's own attachments are set up before anything runs.
  Object.entries(take.attachments ?? {}).forEach(([propId, attachment]) => {
    if (attachment.playerToProp) {
      if (state.has(actorKey(attachment.id)))
        state.set(actorKey(attachment.id), { values: [RIDING + propId], changed: false });
    } else state.set(heldKey(propId), { values: [HELD_BY + attachment.id], changed: false });
  });
  if (take.mainCamera?.cameraId)
    state.set("camera", { values: [take.mainCamera.cameraId], changed: false });
  return state;
}

/**
 * Where the take's actors are when it ends: its own state, then On Take Start,
 * its interactions and On Take End (which runs as the next take starts). If and
 * Switch branches are skipped; parallel branches that leave an actor in more
 * than one place in no fixed order give more than one value. actorId → place,
 * see parseActorPlace.
 */
export function getTakeEndActorPlaces(take: Take3D): Record<string, StateValue> {
  const state = getTakeSetupState(take);
  const context: MoveContext = { ridePropIds: getRidePropIds(take) };
  const graph = take.sequence ? new Graph(take.sequence) : undefined;
  const onStart = graph?.triggerNodeId(ON_TAKE_START);
  if (take.sequence && onStart) applyWholeSequence(state, take.sequence, context, onStart);
  (take.interactions ?? []).forEach((interaction) => applyInteraction(state, interaction, context));
  const onEnd = graph?.triggerNodeId(ON_TAKE_END);
  if (take.sequence && onEnd) applyWholeSequence(state, take.sequence, context, onEnd);

  const places: Record<string, StateValue> = {};
  state.forEach((value, key) => {
    if (key.startsWith("actor:")) places[key.slice("actor:".length)] = value;
  });
  return places;
}

export function getSequenceStateAfterNode(
  take: Take3D,
  sequence: ISequence,
  nodeId: string,
): SequenceStateAtNode {
  const state = getTakeSetupState(take);
  const context: MoveContext = { ridePropIds: getRidePropIds(take, sequence) };

  const isTakeSequence = sequence === take.sequence || sequence.id === take.sequence?.id;
  const graph = new Graph(sequence);
  const trigger = isTakeSequence ? graph.triggerOf(nodeId) : undefined;

  // Before the interactions run: nothing more for On Take Start.
  if (!isTakeSequence || trigger === ON_TAKE_END) {
    const takeGraph = take.sequence ? new Graph(take.sequence) : undefined;
    const onStart = takeGraph?.triggerNodeId(ON_TAKE_START);
    if (take.sequence && onStart) applyWholeSequence(state, take.sequence, context, onStart);

    const interactions = take.interactions ?? [];
    const index = interactions.findIndex((interaction) => interaction.id === sequence.id);
    const before = isTakeSequence || index < 0 ? interactions : interactions.slice(0, index);
    before.forEach((interaction) => applyInteraction(state, interaction, context));
  }

  const skipped = graph.branchOnlyNodes(nodeId);
  const ancestors = graph.ancestorsOf(nodeId);
  // The branches beside the node count as done; If and Switch branches are alternatives, not parallel.
  applyPathAndParallelBranches(
    state,
    graph,
    [...ancestors, nodeId].filter((id) => !skipped.has(id)),
    [...graph.parallelTo(nodeId, ancestors)].filter((id) => !skipped.has(id)),
    context,
  );

  const result: SequenceStateAtNode = { actors: {}, heldProps: {} };
  state.forEach((value, key) => {
    if (key === "camera") result.camera = value;
    else if (key.startsWith("held:")) result.heldProps[key.slice("held:".length)] = value;
    else result.actors[key.slice("actor:".length)] = value;
  });
  return result;
}
